import { useState } from 'react';
import { FiMinus, FiPlus, FiShoppingCart, FiTrash2 } from 'react-icons/fi';

import { Button } from '@hubmaster/ui/Button';
import { toast } from 'sonner';

import { useApp } from '../context/app';
import { CartItem, useCart } from '../context/cart';
import { useCartMode } from '../hooks/useCartMode';
import { generateOrderExcel } from '../lib/generateOrderExcel';
import { generateOrderPDF } from '../lib/generateOrderPDF';
import { Image } from './Image';
import { QuantityInput } from './QuantityInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const fmt = (v: number) =>
	v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function Cart() {
	const [isOpen, setIsOpen] = useState(false);
	const [pendingRemove, setPendingRemove] = useState<CartItem | null>(null);
	const [exportOpen, setExportOpen] = useState(false);
	const mode = useCartMode();
	const { isPurchaseOrder } = mode;
	const { connection } = useApp();
	const {
		items,
		totalItems,
		totalPrice,
		totalCost,
		updateQuantity,
		removeItem,
		addItem,
		clearCart,
	} = useCart();

	const confirmRemove = () => {
		if (!pendingRemove) return;
		const removed = pendingRemove;
		removeItem(removed.id);
		setPendingRemove(null);
		toast(mode.toastRemoved, {
			description: removed.name,
			action: {
				label: 'Desfazer',
				onClick: () =>
					addItem(
						{
							id: removed.id,
							barcode: removed.barcode,
							name: removed.name,
							price: removed.price,
							coast: removed.coast,
							stock: removed.stock,
							unit: removed.unit,
							isWeight: removed.isWeight,
						},
						removed.quantity,
					),
			},
		});
	};

	const buildArgs = () => {
		const now = new Date();
		const datetime = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
		return {
			title: mode.cartTitle,
			isPurchaseOrder: mode.isPurchaseOrder,
			generatedAt: datetime,
			emitente: connection.business,
		};
	};

	const handleExportPDF = async () => {
		const args = buildArgs();
		const { share } = generateOrderPDF({ pdf: args, items });
		setExportOpen(false);
		await share();
	};

	const handleExportExcel = async () => {
		const args = buildArgs();
		const { share } = await generateOrderExcel({ excel: args, items });
		setExportOpen(false);
		await share();
	};

	return (
		<>
			{/* Botão flutuante do carrinho */}
			<Button
				onClick={() => setIsOpen(true)}
				className="fixed bottom-4 right-4 overflow-visible rounded-full p-0"
				size={'iconXl'}
			>
				<FiShoppingCart />
				{totalItems > 0 && (
					<span className="absolute -right-2 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-sm font-bold">
						{totalItems}
					</span>
				)}
			</Button>

			{/* Confirmação de remoção */}
			<Dialog
				open={!!pendingRemove}
				onOpenChange={(open) => !open && setPendingRemove(null)}
			>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Remover item?</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-gray-600">
						Deseja remover{' '}
						<span className="font-semibold">{pendingRemove?.name}</span>{' '}
						{mode.removeDialogContext}?
					</p>
					<div className="flex gap-3 pt-2">
						<Button
							variant="outline"
							className="flex-1"
							onClick={() => setPendingRemove(null)}
						>
							Cancelar
						</Button>
						<Button className="flex-1" onClick={confirmRemove}>
							Remover
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Escolha de formato de exportação */}
			<Dialog open={exportOpen} onOpenChange={setExportOpen}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Exportar como</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-gray-500">
						Escolha o formato para compartilhar ou baixar o{' '}
						{mode.cartTitle.toLowerCase()}.
					</p>
					<div className="flex gap-3 pt-2">
						<Button
							variant="outline"
							className="flex-1"
							onClick={handleExportPDF}
						>
							PDF
						</Button>
						<Button className="flex-1" onClick={handleExportExcel}>
							Excel
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Carrinho */}
			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="flex h-[90vh] max-w-2xl flex-col gap-0 p-0">
					<DialogHeader className="shrink-0 border-b px-6 py-4">
						<DialogTitle className="text-xl">{mode.cartTitle}</DialogTitle>
					</DialogHeader>

					{/* Conteúdo scrollável */}
					<div className="min-h-0 flex-1 overflow-y-auto p-4">
						{items.length === 0 ? (
							<div className="flex h-full flex-col items-center justify-center text-gray-400">
								<FiShoppingCart size={64} className="mb-4" />
								<p className="text-lg">{mode.emptyMessage}</p>
							</div>
						) : (
							<div className="space-y-3">
								{items.map((item) => {
									const itemMargin =
										item.coast != null && item.coast > 0
											? (
													((item.price - item.coast) / item.coast) *
													100
												).toFixed(1)
											: null;

									return (
										<div
											key={item.id}
											className="flex gap-3 rounded-lg border p-3 shadow-sm"
										>
											{/* Imagem do produto */}
											<Image
												src={`https://cdn-cosmos.bluesoft.com.br/products/${item.barcode}`}
												alt={item.name}
												className="h-16 w-16 shrink-0 rounded-md bg-gray-50"
												typeLoading="skeleton"
											/>

											{/* Info + controles */}
											<div className="flex min-w-0 flex-1 flex-col gap-2">
												{/* Linha 1: nome + botão remover */}
												<div className="flex items-start justify-between gap-2">
													<div className="min-w-0 flex-1">
														<h3 className="truncate font-semibold text-gray-900">
															{item.name}
														</h3>
														<p className="text-sm text-gray-500">
															Cód: {item.barcode}
														</p>
													</div>
													<Button
														onClick={() => setPendingRemove(item)}
														variant="outline"
														size="icon"
														className="h-8 w-8 shrink-0 text-red-500 hover:bg-red-50 hover:text-red-600"
													>
														<FiTrash2 size={16} />
													</Button>
												</div>

												{/* Info custo/margem/estoque — só no modo pedido */}
												{isPurchaseOrder && (
													<div className="flex flex-wrap gap-x-3 text-xs text-gray-500">
														<span>
															Custo:{' '}
															{item.coast != null ? fmt(item.coast) : '—'}
														</span>
														{itemMargin != null && (
															<span
																className={
																	Number(itemMargin) >= 0
																		? 'font-medium text-green-600'
																		: 'font-medium text-red-600'
																}
															>
																Margem: {itemMargin}%
															</span>
														)}
														{item.stock != null && (
															<span>
																Est: {item.stock} {item.unit ?? 'un'}
															</span>
														)}
													</div>
												)}

												{/* Linha 2: controles + subtotal */}
												<div className="flex items-center justify-between gap-2">
													<div className="flex items-center gap-1">
														<Button
															onClick={() =>
																updateQuantity(
																	item.id,
																	item.isWeight
																		? Number((item.quantity - 0.1).toFixed(2))
																		: item.quantity - 1,
																)
															}
															variant="outline"
															size="icon"
															className="h-8 w-8 shrink-0"
														>
															<FiMinus size={14} />
														</Button>

														<QuantityInput
															value={item.quantity}
															decimals={item.isWeight ? 2 : 0}
															onChange={(v) => updateQuantity(item.id, v)}
															className="h-8 w-16 rounded border text-center font-semibold focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
														/>

														<Button
															onClick={() =>
																updateQuantity(
																	item.id,
																	item.isWeight
																		? Number((item.quantity + 0.1).toFixed(2))
																		: item.quantity + 1,
																)
															}
															variant="outline"
															size="icon"
															className="h-8 w-8 shrink-0"
														>
															<FiPlus size={14} />
														</Button>
													</div>

													{/* Subtotal */}
													<div className="-mt-2 flex flex-col items-end gap-0">
														{isPurchaseOrder && item.coast != null ? (
															<>
																<span className="text-xs text-gray-400">
																	{item.quantity}× {fmt(item.coast)} (custo)
																</span>
																<p className="shrink-0 text-base font-bold text-primary">
																	{fmt(item.coast * item.quantity)}
																</p>
																<span className="text-xs text-gray-400">
																	Venda: {fmt(item.price * item.quantity)}
																</span>
															</>
														) : (
															<>
																<span className="text-sm text-gray-500">
																	{item.quantity}×{' '}
																	{item.price.toLocaleString('pt-BR', {
																		style: 'currency',
																		currency: 'BRL',
																	})}
																	{item.isWeight && '/kg'}
																</span>
																<p className="shrink-0 text-base font-bold text-gray-900">
																	{(item.price * item.quantity).toLocaleString(
																		'pt-BR',
																		{ style: 'currency', currency: 'BRL' },
																	)}
																</p>
															</>
														)}
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{/* Footer */}
					{items.length > 0 && (
						<div className="shrink-0 border-t p-4">
							{isPurchaseOrder ? (
								<div className="mb-3 space-y-1">
									<div className="flex items-center justify-between text-sm text-gray-600">
										<span>Total Custo:</span>
										<span className="font-semibold">{fmt(totalCost)}</span>
									</div>
									<div className="flex items-center justify-between text-sm text-gray-600">
										<span>Total Venda:</span>
										<span className="font-semibold">{fmt(totalPrice)}</span>
									</div>
									{totalCost > 0 && (
										<div className="flex items-center justify-between border-t pt-1">
											<span className="font-semibold text-gray-700">
												Margem:
											</span>
											<span
												className={`text-xl font-bold ${totalPrice >= totalCost ? 'text-green-600' : 'text-red-600'}`}
											>
												{(((totalPrice - totalCost) / totalCost) * 100).toFixed(
													1,
												)}
												%
											</span>
										</div>
									)}
								</div>
							) : (
								<div className="mb-3 flex items-center justify-between">
									<span className="text-lg font-semibold text-gray-700">
										Total:
									</span>
									<span className="text-3xl font-bold text-primary">
										{totalPrice.toLocaleString('pt-BR', {
											style: 'currency',
											currency: 'BRL',
										})}
									</span>
								</div>
							)}
							<div className="flex gap-3">
								<Button
									onClick={clearCart}
									variant="outline"
									className="flex-1"
								>
									{mode.clearButton}
								</Button>
								<Button onClick={() => setExportOpen(true)} className="flex-1">
									{mode.finalizeButton}
								</Button>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
